import {
  BadgeCheck,
  Globe2,
  Mail,
  Phone,
  Send,
  Zap,
  Sparkles,
  Target,
  Calendar,
  Info,
  ArrowRight,
} from "lucide-react";

/* --- Data ---------------------------------------------------------- */

const SOURCES = [
  {
    id: "src-trade-show",
    label: "Trade Show Import",
    Icon: BadgeCheck,
    desc: "Badge-scan CSV imported from last event: MedTech Summit 2026",
    count: 143,
    status: { tone: "teal", label: "Active" },
    note: "Nurture triggered: Day 0",
    accent: "#00C2A8",
    tag: "#1 SOURCE",
  },
  {
    id: "src-web-form",
    label: "Inbound Web Form",
    Icon: Globe2,
    desc: "HubSpot form → Make.com webhook → CRM",
    count: 89,
    status: { tone: "teal", label: "Active" },
    note: "Routed to sequence on submit",
    accent: "#0EA5E9",
  },
  {
    id: "src-outreach",
    label: "Outreach Replies",
    Icon: Mail,
    desc: "LinkedIn InMail + email replies captured via n8n",
    count: 41,
    status: { tone: "blue", label: "Processing", pulse: true },
    note: "12 pending classification",
    accent: "#F59E0B",
  },
];

const ROWS = [
  { id: "row-1", name: "Dr. Sarah Chen", company: "Midwest Health Systems", source: "Trade Show", score: 87, stage: "Warm", seq: "7-Day Voice+Email+SMS", status: { tone: "teal", label: "Active" } },
  { id: "row-2", name: "James Park", company: "NovaMed Instruments", source: "Web Form", score: 94, stage: "Hot", seq: "Fast-Track (3-day)", status: { tone: "green", label: "Converted" } },
  { id: "row-3", name: "Dr. Marcus Webb", company: "Regional Surgical Group", source: "LinkedIn Reply", score: 72, stage: "Warm", seq: "7-Day Voice+Email+SMS", status: { tone: "teal", label: "Active" } },
  { id: "row-4", name: "Amanda Torres", company: "Valley Medical Supply", source: "Trade Show", score: 45, stage: "Cold", seq: "14-Day Email Only", status: { tone: "teal", label: "Active" } },
  { id: "row-5", name: "Dr. Lisa Tran", company: "Summit Orthopedics", source: "Inbound", score: 91, stage: "Hot", seq: "Fast-Track (3-day)", status: { tone: "green", label: "Booked" } },
  { id: "row-6", name: "Kevin Marsh", company: "Heartland Hospital", source: "Trade Show", score: 38, stage: "Cold", seq: "14-Day Email Only", status: { tone: "red", label: "Stalled" } },
  { id: "row-7", name: "Dr. Priya Nair", company: "Pacific Surgical", source: "LinkedIn Reply", score: 68, stage: "Warm", seq: "7-Day Voice+Email+SMS", status: { tone: "blue", label: "Processing", pulse: true } },
  { id: "row-8", name: "Robert Kim", company: "Great Lakes Devices", source: "Web Form", score: 55, stage: "Cold", seq: "14-Day Email Only", status: { tone: "teal", label: "Active" } },
];

const SCORING_FACTORS = [
  { pts: "+30", label: "Job title match", detail: "surgeon, hospital admin, procurement lead" },
  { pts: "+10–25", label: "Source quality", detail: "trade show > inbound > outreach" },
  { pts: "+15", label: "Company size / territory fit", detail: "target ICP alignment" },
  { pts: "+10–20", label: "Engagement signal", detail: "opened email, clicked link, replied" },
  { pts: "+10", label: "Recency", detail: "last 30 days of event/contact" },
];

const LANES = [
  {
    id: "lane-hot",
    range: "80–100",
    tier: "Hot",
    accent: "#00C2A8",
    title: "Fast-Track Sequence",
    steps: [
      { Icon: Phone, label: "Voice Call", day: "Day 1" },
      { Icon: Send, label: "SMS", day: "Day 2" },
      { Icon: Mail, label: "Email", day: "Day 3" },
    ],
    target: "Demo booked in 72 hrs",
  },
  {
    id: "lane-warm",
    range: "50–79",
    tier: "Warm",
    accent: "#0EA5E9",
    title: "7-Day Sequence",
    steps: [
      { Icon: Mail, label: "Email", day: "Day 1" },
      { Icon: Send, label: "SMS", day: "Day 3" },
      { Icon: Phone, label: "Voice", day: "Day 5" },
      { Icon: Mail, label: "Email", day: "Day 7" },
    ],
    target: "Reach every warm lead 4x",
  },
  {
    id: "lane-cold",
    range: "0–49",
    tier: "Cold",
    accent: "#F59E0B",
    title: "14-Day Email Nurture",
    steps: [
      { Icon: Mail, label: "Email", day: "Day 1" },
      { Icon: Mail, label: "Email", day: "Day 4" },
      { Icon: Mail, label: "Email", day: "Day 7" },
      { Icon: Mail, label: "Email", day: "Day 10" },
      { Icon: Mail, label: "Email", day: "Day 13" },
    ],
    target: "Re-score on engagement",
  },
];

/* --- Small helpers ------------------------------------------------- */

function Pill({ tone, label, pulse }) {
  const cls = {
    teal: "pill pill-teal",
    blue: "pill pill-blue",
    amber: "pill pill-amber",
    green: "pill pill-green",
    red: "pill pill-red",
    muted: "pill pill-muted",
  }[tone];
  return (
    <span className={cls}>
      <span className={`pill-dot ${pulse ? "pill-dot-pulse" : ""}`} />
      {label}
    </span>
  );
}

function stageColor(stage) {
  if (stage === "Hot") return "#00C2A8";
  if (stage === "Warm") return "#0EA5E9";
  return "#7A9BB5";
}

function scoreColor(score) {
  if (score >= 80) return "#00C2A8";
  if (score >= 50) return "#0EA5E9";
  return "#F59E0B";
}

/* --- Sub-components ----------------------------------------------- */

function IntakeSourceCards() {
  return (
    <section data-testid="intake-sources" className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {SOURCES.map((s, i) => {
        const { Icon } = s;
        return (
          <article
            key={s.id}
            data-testid={s.id}
            className="card-glass card-interactive p-5 enter-up"
            style={{ animationDelay: `${i * 70}ms`, position: "relative" }}
          >
            {s.tag && (
              <span
                style={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  color: "#F59E0B",
                  background: "rgba(245, 158, 11, 0.10)",
                  border: "1px solid rgba(245, 158, 11, 0.30)",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                {s.tag}
              </span>
            )}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center rounded-lg"
                style={{
                  width: 36,
                  height: 36,
                  background: `${s.accent}12`,
                  border: `1px solid ${s.accent}30`,
                }}
              >
                <Icon size={16} color={s.accent} strokeWidth={2} />
              </div>
              <div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "var(--text-primary)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {s.label}
                </div>
                <Pill {...s.status} />
              </div>
            </div>

            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "var(--text-secondary)",
                marginBottom: 16,
                minHeight: 38,
              }}
            >
              {s.desc}
            </p>

            <div className="flex items-end justify-between">
              <div>
                <div className="stat-value" style={{ fontSize: 26 }} data-testid={`${s.id}-count`}>
                  {s.count}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.10em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    marginTop: 2,
                  }}
                >
                  Leads
                </div>
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10.5,
                  color: s.accent,
                  textAlign: "right",
                  maxWidth: 150,
                  lineHeight: 1.4,
                }}
              >
                {s.note}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function LeadScoringTable() {
  return (
    <article data-testid="lead-scoring-table" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Target size={14} color="#00C2A8" />
          </div>
          <div>
            <h2
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "var(--text-primary)",
                letterSpacing: "-0.01em",
              }}
            >
              Lead Scoring Queue
            </h2>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.12em",
                color: "var(--text-muted)",
                textTransform: "uppercase",
              }}
            >
              8 leads · auto-scored · last 24h
            </div>
          </div>
        </div>
        <Pill tone="teal" label="Live scoring" pulse />
      </header>

      <div style={{ overflowX: "auto" }}>
        <table
          data-testid="scoring-table"
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}
        >
          <thead>
            <tr>
              {["Name", "Company", "Source", "Score", "Stage", "Assigned Sequence", "Status"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    borderBottom: "1px solid var(--border-divider)",
                    fontWeight: 500,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r, i) => (
              <tr
                key={r.id}
                data-testid={r.id}
                className="enter-up"
                style={{
                  animationDelay: `${i * 40}ms`,
                  transition: "background 150ms ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(22, 35, 51, 0.5)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)", color: "var(--text-primary)", fontWeight: 500 }}>
                  {r.name}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)", color: "var(--text-secondary)" }}>
                  {r.company}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: "rgba(30, 48, 72, 0.5)",
                    }}
                  >
                    {r.source}
                  </span>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <div className="flex items-center gap-2" style={{ minWidth: 110 }}>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontWeight: 600,
                        fontSize: 13,
                        color: scoreColor(r.score),
                        minWidth: 42,
                      }}
                    >
                      {r.score}
                      <span style={{ color: "var(--text-muted)", fontSize: 10 }}>/100</span>
                    </span>
                    <div className="progress-track" style={{ width: 50 }}>
                      <div
                        className="progress-fill"
                        style={{
                          width: `${r.score}%`,
                          background:
                            r.score >= 80
                              ? "linear-gradient(90deg, #00C2A8, #0EA5E9)"
                              : r.score >= 50
                              ? "#0EA5E9"
                              : "#F59E0B",
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11,
                      color: stageColor(r.stage),
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {r.stage}
                  </span>
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)", color: "var(--text-secondary)", fontSize: 12 }}>
                  {r.seq}
                </td>
                <td style={{ padding: "13px 12px", borderBottom: "1px solid rgba(30, 48, 72, 0.4)" }}>
                  <Pill {...r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function ScoringMethodology() {
  return (
    <aside
      data-testid="scoring-methodology"
      className="card-glass p-5"
      style={{ position: "sticky", top: 88 }}
    >
      <header className="flex items-center gap-2.5 mb-4">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.25)" }}
        >
          <Sparkles size={14} color="#0EA5E9" />
        </div>
        <div>
          <h3
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "var(--text-primary)",
              letterSpacing: "-0.01em",
            }}
          >
            How Leads Are Scored
          </h3>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.12em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
            }}
          >
            Model · v2.4
          </div>
        </div>
      </header>

      <ul className="space-y-3">
        {SCORING_FACTORS.map((f, i) => (
          <li
            key={i}
            data-testid={`factor-${i}`}
            className="enter-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-baseline gap-2">
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#00C2A8",
                  minWidth: 46,
                }}
              >
                {f.pts}
              </span>
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                }}
              >
                {f.label}
              </div>
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginLeft: 54,
                lineHeight: 1.4,
                marginTop: 2,
              }}
            >
              {f.detail}
            </div>
          </li>
        ))}
      </ul>

      <div
        className="mt-5 pt-4 flex items-start gap-2"
        style={{ borderTop: "1px solid var(--border-divider)" }}
      >
        <Info size={12} color="#7A9BB5" style={{ marginTop: 2, flexShrink: 0 }} />
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 11.5,
            lineHeight: 1.5,
            color: "var(--text-secondary)",
            fontStyle: "italic",
          }}
        >
          Scores update automatically as the lead engages with the nurture sequence.
        </p>
      </div>
    </aside>
  );
}

function SequenceRouter() {
  return (
    <section data-testid="sequence-router" className="card-glass p-6">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Zap size={14} color="#00C2A8" />
          </div>
          <div>
            <h2
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "var(--text-primary)",
                letterSpacing: "-0.01em",
              }}
            >
              Sequence Router
            </h2>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.12em",
                color: "var(--text-muted)",
                textTransform: "uppercase",
              }}
            >
              Score-based auto-routing
            </div>
          </div>
        </div>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 4,
            color: "#00C2A8",
            background: "rgba(0, 194, 168, 0.08)",
            border: "1px solid rgba(0, 194, 168, 0.20)",
          }}
        >
          Runs on every new lead
        </span>
      </header>

      <div className="space-y-5">
        {LANES.map((lane, li) => (
          <div
            key={lane.id}
            data-testid={lane.id}
            className="enter-up"
            style={{ animationDelay: `${li * 90}ms` }}
          >
            <div
              className="flex flex-col lg:flex-row lg:items-stretch gap-3"
              style={{
                padding: 16,
                borderRadius: 10,
                background: "rgba(15, 25, 35, 0.5)",
                border: `1px solid var(--border-divider)`,
                borderLeftWidth: 3,
                borderLeftColor: lane.accent,
              }}
            >
              {/* Score range */}
              <div
                className="flex-shrink-0"
                style={{ minWidth: 140 }}
              >
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  Score {lane.range}
                </div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: 20,
                    color: lane.accent,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  {lane.tier}
                </div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 12,
                    color: "var(--text-primary)",
                    fontWeight: 500,
                    marginTop: 6,
                  }}
                >
                  {lane.title}
                </div>
              </div>

              {/* Arrow into flow */}
              <div className="hidden lg:flex items-center" style={{ color: lane.accent }}>
                <ArrowRight size={16} />
              </div>

              {/* Steps */}
              <div className="flex-1 flex flex-wrap items-center gap-2">
                {lane.steps.map((step, si) => {
                  const { Icon } = step;
                  const isLast = si === lane.steps.length - 1;
                  return (
                    <div key={si} className="flex items-center gap-2">
                      <div
                        data-testid={`${lane.id}-step-${si}`}
                        className="flex items-center gap-2"
                        style={{
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "var(--bg-elevated)",
                          border: `1px solid ${lane.accent}30`,
                          minWidth: 106,
                        }}
                      >
                        <Icon size={13} color={lane.accent} />
                        <div>
                          <div
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: 12,
                              fontWeight: 500,
                              color: "var(--text-primary)",
                              lineHeight: 1.1,
                            }}
                          >
                            {step.label}
                          </div>
                          <div
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 10,
                              color: "var(--text-muted)",
                              marginTop: 2,
                            }}
                          >
                            {step.day}
                          </div>
                        </div>
                      </div>
                      {!isLast && (
                        <div
                          className="flex items-center"
                          style={{ color: lane.accent, opacity: 0.7 }}
                        >
                          <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
                            <line
                              x1="0" y1="5" x2="18" y2="5"
                              stroke={lane.accent}
                              strokeWidth="1.5"
                              strokeDasharray="3 3"
                            />
                            <path d="M15 1 L20 5 L15 9" stroke={lane.accent} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Target */}
              <div
                className="flex-shrink-0 flex flex-col justify-center"
                style={{
                  minWidth: 200,
                  paddingLeft: 12,
                  borderLeft: "1px solid var(--border-divider)",
                }}
              >
                <div
                  className="flex items-center gap-1.5 mb-1"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  <Calendar size={11} />
                  Target
                </div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: lane.accent,
                    lineHeight: 1.35,
                  }}
                >
                  {lane.target}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function LeadIntake() {
  return (
    <div data-testid="lead-intake-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="lead-intake-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #00C2A8, #0EA5E9)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Ingest & Score
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Lead Intake &amp; Scoring
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 780,
              }}
            >
              Every MQL enters here. The system scores it, tags it, and routes it into the right nurture sequence automatically — no manual triage.
            </p>
          </div>
        </div>
      </section>

      {/* Intake sources */}
      <IntakeSourceCards />

      {/* Table + methodology */}
      <section className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-9">
          <LeadScoringTable />
        </div>
        <div className="col-span-12 xl:col-span-3">
          <div style={{ maxWidth: 320 }}>
            <ScoringMethodology />
          </div>
        </div>
      </section>

      {/* Sequence router */}
      <SequenceRouter />
    </div>
  );
}
